import type {Component, Editor} from 'grapesjs';
import type {StyleAttributes} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getPrimaryTableComponent} from '@EmailMakerReact/lib/email-maker-component-query-library';

/** Table-cell styling (borders, background, spacing, global cell styles) for the React email maker. */

/** Custom pmw-cell-* style props a table carries for its cells. */
const CELL_BORDER_WIDTH_STYLE_PROP = 'pmw-cell-border-width';
const CELL_BORDER_RADIUS_STYLE_PROP = 'pmw-cell-border-radius';
const CELL_BORDER_COLOR_STYLE_PROP = 'pmw-cell-border-color';
const CELL_BACKGROUND_COLOR_STYLE_PROP = 'pmw-cell-background-color';

export interface ApplyGlobalCellStylesOptions {
  cell: Component;
  /** The component carrying the pmw-cell-* styles; falls back to the current selection. */
  parent?: Component;
}

export interface CellSizePropertyOptions {
  cell: Component;
  sliderVal: number;
  /** When set, the update is skipped if the cell's own style diverges from the table-wide pmw-cell-* style. */
  global?: boolean;
}

export interface CellColorPropertyOptions {
  cell: Component;
  color: string;
  global?: boolean;
}

/** Copies the parent's pmw-cell-* styles onto the cell. */
export function applyGlobalCellStyles(editor: Editor, options: ApplyGlobalCellStylesOptions): void {
  const parent = options.parent ?? editor.getSelected();
  if (!parent) {
    return;
  }

  const styles = parent.getStyle();
  const borderWidth = styles[CELL_BORDER_WIDTH_STYLE_PROP];

  if (typeof borderWidth === 'string' && borderWidth) {
    updateCellBorderWidth(editor, {
      sliderVal: parseInt(borderWidth, 10),
      cell: options.cell,
      global: true,
    });
  }

  const borderRadius = styles[CELL_BORDER_RADIUS_STYLE_PROP];

  if (typeof borderRadius === 'string' && borderRadius) {
    updateCellBorderRadius(editor, {
      sliderVal: parseInt(borderRadius, 10),
      cell: options.cell,
      global: true,
    });
  }

  const backgroundColor = styles[CELL_BACKGROUND_COLOR_STYLE_PROP];

  if (typeof backgroundColor === 'string') {
    updateCellBackgroundColor(editor, {
      color: backgroundColor,
      cell: options.cell,
    });
  }

  const borderColor = styles[CELL_BORDER_COLOR_STYLE_PROP];

  if (typeof borderColor === 'string') {
    updateCellBorderColor(editor, {
      color: borderColor,
      cell: options.cell,
      global: true,
    });
  }
}

export function updateCellBorderWidth(editor: Editor, options: CellSizePropertyOptions): void {
  const component = editor.getSelected();

  if (!component) {
    return;
  }

  if (options.global && !isGlobalStyleChangeApplicable(component, options.cell, CELL_BORDER_WIDTH_STYLE_PROP, 'border-width')) {
    return;
  }

  const style: StyleAttributes = {};
  style['border-width'] = `${options.sliderVal}px`;
  style['border-style'] = 'solid';
  options.cell.addStyle(style);
}

/** Applies the border width across every cell of the selection's primary table. */
export function updateCellsBorderWidth(editor: Editor, sliderVal: number): void {
  forEachCellOfPrimaryTable(editor, editor.getSelected(), (cell) => {
    updateCellBorderWidth(editor, {
      sliderVal,
      cell,
      global: true,
    });
  });
}

export function updateCellBorderColor(editor: Editor, options: CellColorPropertyOptions): void {
  const component = editor.getSelected();

  if (!component) {
    return;
  }

  if (options.global && !isGlobalStyleChangeApplicable(component, options.cell, CELL_BORDER_COLOR_STYLE_PROP, 'border-color')) {
    return;
  }

  const style: StyleAttributes = {};
  style['border-color'] = options.color;
  options.cell.addStyle(style);
}

export function updateCellsBorderColor(editor: Editor, color: string): void {
  forEachCellOfPrimaryTable(editor, editor.getSelected(), (cell) => {
    updateCellBorderColor(editor, {color, cell, global: true});
  });
}

/** Background intentionally has no `global` divergence check (unlike border properties). */
export function updateCellBackgroundColor(editor: Editor, options: Omit<CellColorPropertyOptions, 'global'>): void {
  const component = editor.getSelected();

  if (!component) {
    return;
  }

  const style: StyleAttributes = {};
  style['background-color'] = options.color;
  options.cell.addStyle(style);
}

export function updateCellsBackgroundColor(editor: Editor, color: string): void {
  forEachCellOfPrimaryTable(editor, editor.getSelected(), (cell) => {
    updateCellBackgroundColor(editor, {
      color,
      cell,
    });
  });
}

export function updateCellBorderRadius(editor: Editor, options: CellSizePropertyOptions): void {
  const component = editor.getSelected();

  if (!component) {
    return;
  }

  if (options.global && !isGlobalStyleChangeApplicable(component, options.cell, CELL_BORDER_RADIUS_STYLE_PROP, 'border-radius')) {
    return;
  }

  const style: StyleAttributes = {};
  style['border-radius'] = `${options.sliderVal}px`;
  options.cell.addStyle(style);
}

export function updateCellsBorderRadius(editor: Editor, sliderVal: number): void {
  forEachCellOfPrimaryTable(editor, editor.getSelected(), (cell) => {
    updateCellBorderRadius(editor, {
      sliderVal,
      cell,
      global: true,
    });
  });
}

/** Mirrors cellspacing into the mso style prop and the HTML attribute. */
export function updateCellsSpacing(editor: Editor, sliderVal: number): void {
  const selected = editor.getSelected();
  if (!selected) {
    return;
  }

  // addStyle merges the single property rather than replacing all styles via getStyle/setStyle.
  selected.addStyle({'mso-cellspacing': sliderVal});
  selected.addAttributes({
    cellspacing: sliderVal,
  });
}

/** A table-wide update skips cells whose own style diverges from the table's pmw-cell-* style. */
function isGlobalStyleChangeApplicable(table: Component, cell: Component, parentProperty: string, cellProperty: string): boolean {
  const tableStyle = table.getStyle();
  const cellStyle = cell.getStyle();
  const propertiesExist = Object.prototype.hasOwnProperty.call(tableStyle, parentProperty) && Object.prototype.hasOwnProperty.call(cellStyle, cellProperty);
  const propertiesMatch = tableStyle[parentProperty] === cellStyle[cellProperty];

  return !(propertiesExist && !propertiesMatch);
}

/** Iterates every cell of every row of the selection's primary table. */
function forEachCellOfPrimaryTable(editor: Editor, selected: Component | undefined, callback: (cell: Component) => void): void {
  const tableComponent = getPrimaryTableComponent(editor, selected);

  if (!tableComponent) {
    return;
  }

  tableComponent.components().each((row) => {
    row.components().each((cell) => {
      callback(cell);
    });
  });
}
